// NOTE: NSAI-7841 — comparisons a model wrote in its own layout, rewritten
// into the contract's `options` + `attributes`.
//
// Local runs and QA's retest collected these layouts, all carrying the same
// information as the contract's one:
//   - `items` / `rows` / `options` whose rows carry `values`, one per entry of
//     a `columns` list;
//   - `rows` of arrays under `columns`, the first cell naming the option;
//   - rows with nested `attributes: [{ label, value }]`;
//   - rows with one key per attribute (`{ name, price, storage, support }`).
//
// It is a second try, like the JSON repair (NSAI-7852): `parseUiSpec` calls it
// only when the spec as written fails, and draws the result only when it then
// passes the whole contract. Anything ambiguous — a row without a name, a
// missing cell, a column count that does not match — returns null and the
// original error stands.
//
// A comparison already in the contract's layout gets one fix of its own: an
// option `id` the id rule refuses ("google-drive") is renamed and
// `recommended` follows it. An option id is only the key `recommended` points
// at, never read by a formula, so its spelling carries nothing the card needs.
//
// Only a block that says `"type": "comparison"` is touched: the model named
// the block, and only its inner layout differs. Comparison fields written at
// the top level with no `type` stay refused (NSAI-7853) — telling the block
// from its keys would be guessing at a malformed spec.

import {
  MAX_UI_COMPARISON_ATTRIBUTES,
  MAX_UI_COMPARISON_OPTIONS,
  MAX_UI_ID_LENGTH,
  MIN_UI_COMPARISON_OPTIONS,
  UI_COMPARISON_NAME_HEADER_PATTERN,
  UI_COMPARISON_NAME_KEYS,
  UI_COMPARISON_NON_ATTRIBUTE_KEYS,
  UI_COMPARISON_OPTION_ID_PREFIX,
  UI_COMPARISON_ROW_KEYS,
  UI_ID_PATTERN,
  UI_RESERVED_IDENTIFIERS,
} from "../../constants/ui";
import {
  EUiBlockType,
  type IFindUiRecommendedIndexProps,
  type IIsUsableUiIdProps,
  type IReadUiComparisonRowProps,
  type IUiComparisonAttribute,
  type IUiComparisonRow,
  type IZipUiComparisonCellsProps,
  type TUiRecord,
} from "../../types/ui";
import { UI_EXPRESSION_FUNCTION_NAMES } from "./expression";

const FUNCTION_NAMES: ReadonlySet<string> = new Set(
  UI_EXPRESSION_FUNCTION_NAMES,
);

const isRecord = (value: unknown): value is TUiRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readCellText = (value: unknown): string | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }

  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : null;
};

const readColumnLabel = (column: unknown): string | null =>
  isRecord(column) ? readCellText(column.label) : readCellText(column);

// NOTE: "free_storage" and "freeStorage" both read "Free storage".
const humanizeKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

const readColumns = (block: TUiRecord): string[] | null => {
  if (!Array.isArray(block.columns)) {
    return null;
  }

  const labels = block.columns.map(readColumnLabel);

  return labels.every((label) => label !== null)
    ? labels.filter((label): label is string => label !== null)
    : null;
};

const readRowName = (row: TUiRecord): string | null => {
  for (const key of UI_COMPARISON_NAME_KEYS) {
    const name = readCellText(row[key]);

    if (name !== null) {
      return name;
    }
  }

  return null;
};

const zipCells = ({
  labels,
  values,
}: IZipUiComparisonCellsProps): [string, string][] | null => {
  if (labels.length !== values.length) {
    return null;
  }

  const cells = values.map(readCellText);

  return cells.every((cell) => cell !== null)
    ? labels.map((label, index) => [label, cells[index] ?? ""])
    : null;
};

const readNestedAttributes = (
  attributes: unknown[],
): [string, string][] | null => {
  const cells = attributes.map((attribute): [string, string] | null => {
    if (!isRecord(attribute)) {
      return null;
    }

    const label = readCellText(attribute.label ?? attribute.name);
    const value = readCellText(attribute.value ?? attribute.text);

    return label !== null && value !== null ? [label, value] : null;
  });

  return cells.every((cell) => cell !== null)
    ? cells.filter((cell): cell is [string, string] => cell !== null)
    : null;
};

const readKeyedAttributes = (row: TUiRecord): [string, string][] =>
  Object.entries(row).flatMap(([key, value]): [string, string][] => {
    const text = readCellText(value);

    return UI_COMPARISON_NON_ATTRIBUTE_KEYS.has(key) || text === null
      ? []
      : [[humanizeKey(key), text]];
  });

const readArrayRow = ({
  row,
  columns,
}: IReadUiComparisonRowProps): IUiComparisonRow | null => {
  if (!Array.isArray(row) || columns === null) {
    return null;
  }

  const label = readCellText(row[0]);
  // NOTE: Either the columns list only the attributes (one fewer than the
  // cells), or the first column heads the option names ("Plan", "Service").
  // With one column per cell and any other first header, the row may have no
  // name at all — `["$2", "15 GB"]` under `["Price", "Storage"]` — and taking
  // "$2" as an option's name would draw wrong facts, so it is left refused.
  let attributeLabels: string[] | null = null;

  if (columns.length === row.length - 1) {
    attributeLabels = columns;
  } else if (
    columns.length === row.length &&
    UI_COMPARISON_NAME_HEADER_PATTERN.test(columns[0] ?? "")
  ) {
    attributeLabels = columns.slice(1);
  }

  const cells =
    attributeLabels === null
      ? null
      : zipCells({ labels: attributeLabels, values: row.slice(1) });

  return label !== null && cells !== null
    ? { label, cells, isRecommended: false }
    : null;
};

const readObjectRow = ({
  row,
  columns,
}: IReadUiComparisonRowProps): IUiComparisonRow | null => {
  if (!isRecord(row)) {
    return null;
  }

  const label = readRowName(row);

  if (label === null) {
    return null;
  }

  let cells: [string, string][] | null;

  if (Array.isArray(row.values)) {
    cells =
      columns === null
        ? null
        : zipCells({ labels: columns, values: row.values });
  } else if (Array.isArray(row.attributes)) {
    cells = readNestedAttributes(row.attributes);
  } else {
    cells = readKeyedAttributes(row);
  }

  if (cells === null || cells.length === 0) {
    return null;
  }

  const sublabel = readCellText(row.sublabel) ?? undefined;
  const sourceId = readCellText(row.id) ?? undefined;

  return {
    label,
    cells,
    isRecommended: row.recommended === true,
    ...(sublabel !== undefined && { sublabel }),
    ...(sourceId !== undefined && { sourceId }),
  };
};

// NOTE: A row with more cells than a comparison may show, or with two cells
// under one label (a repeated header, or `monthlyPrice` beside
// `monthly_price`), was not understood: keeping one of the two would drop a
// fact the card then shows as complete.
const readRow = (props: IReadUiComparisonRowProps): IUiComparisonRow | null => {
  const row = Array.isArray(props.row)
    ? readArrayRow(props)
    : readObjectRow(props);

  if (row === null || row.cells.length > MAX_UI_COMPARISON_ATTRIBUTES) {
    return null;
  }

  const labels = new Set(row.cells.map(([label]) => label));

  return labels.size === row.cells.length ? row : null;
};

const readRows = (block: TUiRecord): unknown[] | null => {
  for (const key of UI_COMPARISON_ROW_KEYS) {
    const rows = block[key];

    if (Array.isArray(rows)) {
      return rows;
    }
  }

  return null;
};

// NOTE: Every option must carry every attribute; a hole means the layout was
// not understood, and guessing a cell would put a wrong fact in the card.
const buildAttributes = (
  rows: IUiComparisonRow[],
): IUiComparisonAttribute[] | null => {
  const labels = [
    ...new Set(rows.flatMap((row) => row.cells.map(([label]) => label))),
  ];
  const cellsByLabel = rows.map((row) => new Map(row.cells));
  const attributes = labels.map((label) => ({
    label,
    values: cellsByLabel.map((cells) => cells.get(label) ?? null),
  }));

  return attributes.every((attribute) =>
    attribute.values.every((value) => value !== null),
  )
    ? attributes.map((attribute) => ({
        label: attribute.label,
        values: attribute.values.filter(
          (value): value is string => value !== null,
        ),
      }))
    : null;
};

// NOTE: The same rule `idSchema` enforces, plus uniqueness within the block.
const isUsableId = ({ id, taken }: IIsUsableUiIdProps): boolean =>
  id.length > 0 &&
  id.length <= MAX_UI_ID_LENGTH &&
  UI_ID_PATTERN.test(id) &&
  !UI_RESERVED_IDENTIFIERS.has(id) &&
  !FUNCTION_NAMES.has(id) &&
  !taken.has(id);

// NOTE: "google-drive" → "google_drive"; anything that still fails the rule
// (a leading digit, a reserved word, a clash) → the first free "option_N".
const pickOptionId = ({ id, taken }: IIsUsableUiIdProps): string => {
  const spelled = id
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, MAX_UI_ID_LENGTH);

  if (isUsableId({ id: spelled, taken })) {
    return spelled;
  }

  let index = 1;

  while (taken.has(`${UI_COMPARISON_OPTION_ID_PREFIX}${index}`)) {
    index++;
  }

  return `${UI_COMPARISON_OPTION_ID_PREFIX}${index}`;
};

const readOptionId = (option: unknown): string =>
  isRecord(option) && typeof option.id === "string" ? option.id : "";

// NOTE: Two options under one id leave `recommended` ambiguous, so neither is
// renamed; the schema then refuses the repeat with its own message.
const hasRepeatedOptionId = (options: unknown[]): boolean => {
  const ids = options.map(readOptionId).filter((id) => id.length > 0);

  return new Set(ids).size !== ids.length;
};

const repairOptionIds = (block: TUiRecord): TUiRecord | null => {
  // NOTE: Bounded before any work: a block with more options than a
  // comparison may show is refused anyway, and a crafted one must not cost a
  // pass per option.
  if (
    !Array.isArray(block.options) ||
    block.options.length > MAX_UI_COMPARISON_OPTIONS ||
    hasRepeatedOptionId(block.options)
  ) {
    return null;
  }

  // NOTE: Ids that already pass are reserved first, so a renamed option can
  // never take the id of a later one that was written correctly.
  const taken = new Set<string>();

  for (const option of block.options) {
    const id = readOptionId(option);

    if (isUsableId({ id, taken })) {
      taken.add(id);
    }
  }

  const renamed = new Map<string, string>();
  const options = block.options.map((option) => {
    const id = readOptionId(option);

    if (!isRecord(option) || taken.has(id)) {
      return option;
    }

    const newId = pickOptionId({ id, taken });
    taken.add(newId);
    renamed.set(id, newId);

    return { ...option, id: newId };
  });

  if (renamed.size === 0) {
    return null;
  }

  const recommended =
    typeof block.recommended === "string"
      ? (renamed.get(block.recommended) ?? block.recommended)
      : block.recommended;

  return {
    ...block,
    options,
    ...(recommended !== undefined && { recommended }),
  };
};

// NOTE: Which option the block recommends, by the row's own flag or by a
// block-level `recommended` naming a row's original id or its name. -1 is no
// recommendation; null is a `recommended` that matches no single option, which
// leaves the block refused rather than dropping or guessing the pick.
const findRecommendedIndex = ({
  options,
  recommended,
}: IFindUiRecommendedIndexProps): number | null => {
  const recommendedText = readCellText(recommended);

  if (recommendedText === null) {
    return options.findIndex((option) => option.isRecommended);
  }

  const wanted = recommendedText.toLowerCase();
  const matches = options.flatMap((option, index) =>
    option.sourceId?.toLowerCase() === wanted ||
    option.label.toLowerCase() === wanted
      ? [index]
      : [],
  );

  return matches.length === 1 ? (matches[0] ?? null) : null;
};

const reshapeComparisonBlock = (block: TUiRecord): TUiRecord | null => {
  // NOTE: A block that already has `attributes` is in the contract's layout;
  // the only fix it gets is its option ids.
  if (Array.isArray(block.attributes)) {
    return repairOptionIds(block);
  }

  const sourceRows = readRows(block);
  const columns = readColumns(block);

  // NOTE: Counted before any row is read, so a crafted block cannot make the
  // reshape do work past the budget the contract allows.
  if (
    sourceRows === null ||
    sourceRows.length < MIN_UI_COMPARISON_OPTIONS ||
    sourceRows.length > MAX_UI_COMPARISON_OPTIONS ||
    (columns !== null && columns.length > MAX_UI_COMPARISON_ATTRIBUTES + 1)
  ) {
    return null;
  }

  const rows = sourceRows.map((row) => readRow({ row, columns }));

  if (rows.some((row) => row === null)) {
    return null;
  }

  const options = rows.filter((row): row is IUiComparisonRow => row !== null);
  const attributes = buildAttributes(options);

  if (attributes === null) {
    return null;
  }

  const ids = options.map(
    (_, index) => `${UI_COMPARISON_OPTION_ID_PREFIX}${index + 1}`,
  );
  const recommendedIndex = findRecommendedIndex({
    options,
    recommended: block.recommended,
  });

  if (recommendedIndex === null) {
    return null;
  }

  const title = readCellText(block.title) ?? readCellText(block.label);
  const verdict = readCellText(block.verdict);

  return {
    type: EUiBlockType.COMPARISON,
    ...(title !== null && { title }),
    options: options.map((option, index) => ({
      id: ids[index],
      label: option.label,
      ...(option.sublabel !== undefined && { sublabel: option.sublabel }),
    })),
    attributes,
    ...(recommendedIndex >= 0 && { recommended: ids[recommendedIndex] }),
    ...(verdict !== null && { verdict }),
  };
};

// NOTE: Returns the block unchanged when it is not a comparison or its layout
// was not understood, so the contract reports the original problem.
const reshapeBlock = (block: unknown): unknown => {
  if (!isRecord(block)) {
    return block;
  }

  if (block.type === EUiBlockType.TABS && Array.isArray(block.tabs)) {
    return {
      ...block,
      tabs: block.tabs.map((tab) =>
        isRecord(tab) && Array.isArray(tab.blocks)
          ? { ...tab, blocks: tab.blocks.map(reshapeBlock) }
          : tab,
      ),
    };
  }

  return block.type === EUiBlockType.COMPARISON
    ? (reshapeComparisonBlock(block) ?? block)
    : block;
};

export const reshapeUiComparisons = (spec: unknown): unknown =>
  isRecord(spec) && Array.isArray(spec.blocks)
    ? { ...spec, blocks: spec.blocks.map(reshapeBlock) }
    : spec;
